import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

const NPSScoreChart = ({ data, description }) => {
  const navigate = useNavigate();
  
  // Colors for chart
  const COLORS = {
    detractor: '#ef4444',    // Red for detractors (0-6)
    passive: '#f59e0b',      // Yellow for passives (7-8)
    promoter: '#10b981'      // Green for promoters (9-10)
  };

  // Filter out invalid/empty scores first
  const getValidData = () => {
    return data.filter(item => {
      const score = Number(item.npsScore || item.score || item.averageScore || item.value || 0);
      return score > 0 && score <= 10; // Only include valid scores between 1-10
    });
  };

  // Handle bar click to navigate to segment detail
  const handleBarClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const payload = data.activePayload[0].payload;
      let segment = '';
      
      if (payload.name.includes('Detractors')) {
        segment = 'detractors';
      } else if (payload.name.includes('Passives')) {
        segment = 'passives';
      } else if (payload.name.includes('Promoters')) {
        segment = 'promoters';
      }
      
      if (segment && payload.value > 0) {
        navigate(`/nps/${segment}?returnTab=overview`);
      }
    }
  };

  // Data preparation for chart
  const npsScoreData = () => {
    const validData = getValidData();
    
    // NPS categories
    const scoreData = [
      { name: 'Detractors (0-6)', value: 0, color: COLORS.detractor, segment: 'detractors' },
      { name: 'Passives (7-8)', value: 0, color: COLORS.passive, segment: 'passives' },
      { name: 'Promoters (9-10)', value: 0, color: COLORS.promoter, segment: 'promoters' }
    ];
    
    // Count candidates in each NPS category (only valid scores)
    validData.forEach((item) => {
      const score = Number(item.npsScore || item.score || item.averageScore || item.value || 0);
      
      if (score >= 1 && score <= 6) {
        scoreData[0].value++;
      } else if (score >= 7 && score <= 8) {
        scoreData[1].value++;
      } else if (score >= 9 && score <= 10) {
        scoreData[2].value++;
      }
    });
    
    return scoreData;
  };

  // Calculate NPS score (% Promoters - % Detractors) - only from valid responses
  const calculateNPS = () => {
    const validData = getValidData();
    const totalValidResponses = validData.length;
    if (totalValidResponses === 0) return 0;
    
    const counts = npsScoreData();
    const promoterPercentage = (counts[2].value / totalValidResponses) * 100;
    const detractorPercentage = (counts[0].value / totalValidResponses) * 100;
    
    return Math.round(promoterPercentage - detractorPercentage);
  };

  const npsScore = calculateNPS();
  const npsStatus = npsScore >= 50 ? 'Excellent' : npsScore >= 30 ? 'Good' : npsScore >= 0 ? 'Average' : 'Needs Improvement';
  const npsStatusColor = npsScore >= 50 ? 'bg-green-100 text-green-800' : 
                         npsScore >= 30 ? 'bg-blue-100 text-blue-800' : 
                         npsScore >= 0 ? 'bg-yellow-100 text-yellow-800' : 
                         'bg-red-100 text-red-800';

  if (!data || data.length === 0) {
    return (
      <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1200ms'}}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-800">NPS - TP Ratings</h3>
          <div className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-1 rounded-full">
            No Data
          </div>
        </div>
        {description && (
          <p className="text-sm text-gray-600 mb-4">{description}</p>
        )}
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No feedback data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '1200ms'}}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">NPS - TP Ratings</h3>
        <div className={`${npsStatusColor} text-xs font-medium px-2.5 py-1 rounded-full`}>
          {npsStatus}
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      
      <div className="flex justify-center items-center mb-6">
        <div className="text-center">
          <span className="text-3xl font-bold">{npsScore}</span>
          <p className="text-xs text-gray-500 mt-1">NPS Score ({getValidData().length} valid responses)</p>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={npsScoreData()}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          onClick={handleBarClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis />
          <Tooltip
            contentStyle={{ 
              borderRadius: '8px', 
              border: 'none', 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              fontSize: '12px'
            }}
            formatter={(value, name) => [
              `${value} responses${value > 0 ? ' (click to view details)' : ''}`, 
              'Count'
            ]}
          />
          <Bar 
            dataKey="value" 
            radius={[4, 4, 0, 0]}
            style={{ cursor: 'pointer' }}
          >
            {npsScoreData().map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color}
                style={{ 
                  cursor: entry.value > 0 ? 'pointer' : 'default',
                  opacity: entry.value > 0 ? 1 : 0.7
                }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      
      {/* Click hint */}
      {npsScoreData().some(item => item.value > 0) && (
        <div className="text-center mt-2">
          <p className="text-xs text-gray-500">💡 Click on segments to view detailed feedback</p>
        </div>
      )}
    </div>
  );
};

export default NPSScoreChart;
